import { useState } from 'react';
import {
  FAIL_REASON_LABEL,
  isTerminal,
  type OrderStatus,
  GOODS_DISPOSITION_LABEL,
  TRANSITIONS,
  allowedActionsFor,
  ulid,
  type OrderAction,
} from '@delivery/shared';
import { ApiFailure, api } from '../lib/api';
import { money } from '../lib/format';
import { patchCachedOrder } from '../lib/cached';
import type { DriverRow, OrderDetail, ZoneRow } from '../lib/types';
import { ReviewAddressDialog } from './ReviewAddressDialog';
import { MapPin, NotebookPen } from 'lucide-react';
import { InlineIcon } from './InlineIcon';

/**
 * 订单操作。
 *
 * ★ 按钮显隐**从共享的状态机表算出来**,不在这里另写一套 if。
 *   前端自己判断的话,迟早出现「界面上能点、后端拒绝」——
 *   加一个状态要改两个地方,漏一个就是线上 bug。
 *   见 packages/shared/order/transitions.ts 顶部注释。
 *
 * 权限同样来自那张表(每个动作声明了自己需要的 permission),
 * 和当前用户的权限集取交集。
 */

const ACTION_LABEL: Partial<Record<OrderAction, string>> = {
  UNASSIGN: '撤回派单',
  TRANSFER: '改派司机',
  /*
   * ★ 这四个原来在后台**没有按钮**,而 UNASSIGN / TRANSFER / CANCEL / RETURN 有。
   *
   *   状态机里 DELIVER / FAIL / RETRY 早就允许 USER 做了(PICKUP 是这次放开的),
   *   缺的只是这张文案表里的一行 —— 而这张表同时是按钮的显隐依据,
   *   于是「后端允许、界面上没有」,谁都不会发现少了什么。
   *
   *   真实场景:司机手机没电 / 没装 app / 点错了状态 / 三方回调断了。
   *   在这之前调度员的唯一出路是让单子卡着,或者取消重下 ——
   *   而取消重下会换单号、废掉司机已经跑完的那一趟。
   */
  /*
   * ★ 「待接单」的单在后台原来一步都推不动(只有转派/撤回/取消)——
   *   而货常常已经拉走了。代他接一下,后面取货送达才走得通。
   */
  ACCEPT: '代司机接单',
  PICKUP: '标记已取货',
  DELIVER: '标记已送达',
  FAIL: '标记未送达',
  RETRY: '重新投递',
  CANCEL: '取消订单',
  RETURN: '标记退回',
};

/** 危险动作用红色。取消和退回都是不可逆的终态 */
const DANGER: OrderAction[] = ['CANCEL', 'RETURN'];

/**
 * 本该由司机在现场点的那几个。
 *
 * ★ 后台代点是**例外**,不是常规路径 —— 所以要填一句为什么,
 *   而且这句话会进时间轴。没有它的话,事后看这单只能看到
 *   「已送达」,看不出送达是司机报的还是办公室里补的;
 *   而这两者在对账和投诉处理时的分量完全不同。
 */
const ON_BEHALF: OrderAction[] = ['ACCEPT', 'PICKUP', 'DELIVER', 'FAIL', 'RETRY'];

const SELECT =
  'w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600 ' +
  'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100';

export function OrderActions({
  order,
  drivers,
  zones,
  permissions,
  features,
  onDone,
}: {
  order: OrderDetail;
  drivers: DriverRow[];
  zones: ZoneRow[];
  permissions: string[];
  features?: Record<string, boolean>;
  onDone: () => Promise<void>;
}) {
  const [active, setActive] = useState<OrderAction | null>(null);
  const [noting, setNoting] = useState(false);
  const [reviewing, setReviewing] = useState(false);

  const actions = allowedActionsFor(order.status, 'USER')
    .filter((a) => ACTION_LABEL[a])
    .filter((a) => permissions.includes(TRANSITIONS[a].permission));

  return (
    <div className="flex flex-wrap gap-2">
      {permissions.includes('order:update') && (
        <button
          onClick={() => setReviewing(true)}
          className={`px-3 py-1.5 rounded-lg text-sm ${
            order.needsReview
              ? // 缺坐标是硬伤:司机导航不了,派下去也送不到 —— 高亮出来
                'border border-amber-400 dark:border-amber-700 text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30'
              : 'border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300'
          }`}
        >
          <InlineIcon icon={MapPin} /> {order.needsReview ? '补全坐标' : '地址复核'}
        </button>
      )}

      <button
        onClick={() => setNoting(true)}
        className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600
                   text-sm text-slate-700 dark:text-slate-300"
      >
        <InlineIcon icon={NotebookPen} /> 改备注
      </button>

      {actions.map((a) => (
        <button
          key={a}
          onClick={() => setActive(a)}
          className={`px-3 py-1.5 rounded-lg text-sm ${
            DANGER.includes(a)
              ? 'border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400'
              : 'border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300'
          }`}
        >
          {ACTION_LABEL[a]}
        </button>
      ))}

      {actions.length === 0 && (
        <span className="text-sm text-slate-400 self-center">当前状态没有可执行的操作</span>
      )}

      {noting && <NoteDialog order={order} onClose={() => setNoting(false)} onDone={onDone} />}
      {reviewing && (
        <ReviewAddressDialog
          order={order}
          zones={zones}
          onClose={() => setReviewing(false)}
          onDone={onDone}
        />
      )}
      {active && (
        <ActionDialog
          action={active}
          order={order}
          drivers={drivers}
          features={features}
          onClose={() => setActive(null)}
          onDone={onDone}
        />
      )}
    </div>
  );
}

function NoteDialog({
  order,
  onClose,
  onDone,
}: {
  order: OrderDetail;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  /*
   * ★ 预填**当前这条备注**,而不是给一个空框。
   *
   *   用户原话:「备注是可以修改的,订单里面可以修改,
   *   修改的时候我们可以继续加新的东西」。
   *   空框只能追加:想改掉写错的那半句,他只能再写一行
   *   「上面那句不算」—— 而司机在门口读到的是两句矛盾的话。
   *
   * ★ 一个框既能改也能加,所以不再有「加备注」那个入口。
   *   两个入口(一个追加一个改写)会让人每次都要先想一下该点哪个。
   */
  const [note, setNote] = useState(order.receiverNote ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (busy) return;
    setBusy(true);
    try {
      await api(`/orders/${order.id}/note`, { method: 'PATCH', body: { note: note.trim() } });
      await onDone();
      onClose();
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '提交失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell title="改备注" onClose={onClose} error={error}>
      <textarea
        aria-label="备注内容"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={4}
        placeholder="例如:客户来电要求下午 3 点后再送"
        className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600
                   bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
      />
      {/*
        ★ 说清两件事:这条会印在小票上、司机也看得到;
          以及改动本身有记录 —— 前者决定他怎么写,后者让他不必担心改坏了没法追。
      */}
      <p className="mt-1 text-xs text-slate-400">
        这条备注司机看得到,也会印在小票上。每次改动都记入时间轴(谁、什么时候、改成了什么)
      </p>
      <Footer
        busy={busy}
        disabled={note.trim() === (order.receiverNote ?? '').trim()}
        onClose={onClose}
        onSubmit={submit}
      />
    </Shell>
  );
}

function ActionDialog({
  action,
  order,
  drivers,
  features,
  onClose,
  onDone,
}: {
  action: OrderAction;
  order: OrderDetail;
  drivers: DriverRow[];
  features?: Record<string, boolean>;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const def = TRANSITIONS[action];
  const [reason, setReason] = useState('');
  const [driverId, setDriverId] = useState('');
  const [disposition, setDisposition] = useState('RETURNED_TO_SENDER');
  const [failReason, setFailReason] = useState('NO_ONE_HOME');
  const [failDisposition, setFailDisposition] = useState('RETRY');
  const [codMethod, setCodMethod] = useState('CASH');
  const [handedOver, setHandedOver] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /*
   * ★ 撞上照片门槛之后才问「要不要没有照片也记上」,而不是一上来就摆着。
   *
   *   前端不知道这一单的商家配了什么(那是四级优先级算出来的,
   *   只有后端答得了),所以预先摆一句「如果需要拍照…」只会
   *   对绝大多数不需要拍照的单造成噪音,而且写不准。
   *
   *   撞了才问的好处是:看到这句话的人,他这一单**确实**缺凭证,
   *   而且他要再点一次才算数 —— 强推是个显式动作,不是顺手。
   */
  const [proofBlocked, setProofBlocked] = useState<string | null>(null);

  // 已取货的单取消时必须交代货物去向,否则后端 guard 会拒
  const needsDisposition = action === 'CANCEL' && order.pickedUpAt != null;
  const needsDriver = action === 'TRANSFER';
  const needsFailReason = action === 'FAIL';
  /*
   * ★ 这两样后端是**硬门槛**(codCollectedIfNeeded / specialItemsHandedOver):
   *   不问就报错。而报错的时机是人已经点完确认之后 ——
   *   在这里问一句,比让她撞一次「代收金额与订单不符」强。
   */
  const codAmount = Number(order.codAmount ?? 0);
  const needsCod = action === 'DELIVER' && codAmount > 0 && features?.cod !== false;
  const specialItems = action === 'DELIVER' ? (order.specialItems ?? []) : [];
  const onBehalf = ON_BEHALF.includes(action);
  const candidates = drivers.filter((d) => d.id !== order.driver?.id && d.status === 'ACTIVE');

  const ready =
    // 代司机点的动作,原因跟着一起必填 —— 理由见 ON_BEHALF 上面那段
    (!def.requiresReason && !onBehalf ? true : reason.trim().length > 0) &&
    (!needsDriver || driverId !== '') &&
    specialItems.every((i) => handedOver.includes(i.code));

  async function submit() {
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ status: string }>(`/orders/${order.id}/transitions`, {
        method: 'POST',
        body: {
          action,
          idempotencyKey: ulid(),
          // ★ 带上乐观锁版本号:详情打开后别人改过这一单就会被拒,
          //   而不是把对方的改动悄悄覆盖掉
          expectedVersion: order.version,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
          ...(needsDriver ? { driverId } : {}),
          payload: {
            ...(needsDisposition ? { goodsDisposition: disposition } : {}),
            ...(needsFailReason ? { failReason, failDisposition } : {}),
            ...(needsCod ? { codCollected: { amount: codAmount, method: codMethod } } : {}),
            ...(specialItems.length > 0 ? { specialItemsHandedOver: handedOver } : {}),
            /*
             * ★ 后台代点时,那句「为什么代点」同时充当**跳过拍照的理由**。
             *
             *   商家配了「允许说明理由后跳过」的话,这一步就能过;
             *   配了「必须拍照」的话后端照样拒 —— 那道门是商家要的凭证,
             *   给后台开个后门等于所有需要拍照的单都能绕过去。
             *   被拒时错误里写着「该商家的订单需要拍摄商品照片」,
             *   人看得懂下一步该干什么(让司机补拍,或者去改商家配置)。
             */
            ...(onBehalf && reason.trim() ? { proofSkipReason: reason.trim() } : {}),
            /*
             * ★ 只有人**看过警告又点了一次**才带上这个字段。
             *   带上它意味着「没有照片也记」,后端要 order:override_proof,
             *   而且时间轴上会写成「无照片凭证」——
             *   这种痕迹不该由一次普通的点击留下。
             */
            ...(proofBlocked ? { proofOverrideReason: reason.trim() } : {}),
          },
        },
      });

      /*
       * ★ 状态变了,就地把本地缓存里那条也改掉。
       *
       *   用户原话:「结束订单后回到地图上,地图上直接不显示,
       *   这样可以减少点错订单」—— 不改的话,他刚做完一单切到地图,
       *   那个点还在,完全可能再点它一次。
       *
       *   这一改只是让眼前这一刻是对的,下一次请求回来会整份覆盖。
       */
      void patchCachedOrder(order.id, { status: r.status }, isTerminal(r.status as OrderStatus));

      await onDone();
      onClose();
    } catch (e) {
      /*
       * ★ 缺照片单独处理:这不是「操作失败」,是「还差一样东西」——
       *   而那样东西后台拿不到(司机手机没电正是要代点的原因)。
       *   所以把话摆出来让人决定,而不是把她挡在一句红字后面。
       */
      const code = e instanceof ApiFailure ? e.code : '';
      if (
        (code === 'PICKUP_PROOF_REQUIRED' || code === 'DELIVERY_PROOF_REQUIRED') &&
        !proofBlocked
      ) {
        setProofBlocked(e instanceof ApiFailure ? e.message : '这一单需要照片凭证');
        setError(null);
      } else {
        setError(e instanceof ApiFailure ? e.message : '操作失败');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell title={ACTION_LABEL[action] ?? action} onClose={onClose} error={error}>
      {needsDriver && (
        <label className="block mb-3">
          <span className="block mb-1 text-sm text-slate-600 dark:text-slate-400">转给</span>
          <select
            aria-label="转给"
            value={driverId}
            onChange={(e) => setDriverId(e.target.value)}
            className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600
                       bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
          >
            <option value="">选择司机…</option>
            {candidates.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}({d.driverNo})· 手上 {d.currentOrderCount} 单
              </option>
            ))}
          </select>
        </label>
      )}

      {needsDisposition && (
        <label className="block mb-3">
          <span className="block mb-1 text-sm text-slate-600 dark:text-slate-400">
            货物去向(货已被司机取走,必须交代)
          </span>
          <select
            aria-label="货物去向"
            value={disposition}
            onChange={(e) => setDisposition(e.target.value)}
            className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600
                       bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
          >
            {Object.entries(GOODS_DISPOSITION_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>
      )}

      {needsFailReason && (
        <>
          <label className="block mb-3">
            <span className="block mb-1 text-sm text-slate-600 dark:text-slate-400">
              没送到的原因
            </span>
            <select
              aria-label="没送到的原因"
              value={failReason}
              onChange={(e) => setFailReason(e.target.value)}
              className={SELECT}
            >
              {Object.entries(FAIL_REASON_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          {/* ★ 只记原因不问货怎么办,会变成「货在司机车上但没人知道」 */}
          <label className="block mb-3">
            <span className="block mb-1 text-sm text-slate-600 dark:text-slate-400">
              货怎么处理
            </span>
            <select
              aria-label="货怎么处理"
              value={failDisposition}
              onChange={(e) => setFailDisposition(e.target.value)}
              className={SELECT}
            >
              <option value="RETRY">留在车上,改天再送</option>
              <option value="RETURN_TO_SENDER">退回发货方</option>
            </select>
          </label>
        </>
      )}

      {needsCod && (
        <label className="block mb-3">
          <span className="block mb-1 text-sm text-slate-600 dark:text-slate-400">
            这一单要代收 {money(order.codAmount)} —— 钱怎么收的
          </span>
          <select
            aria-label="代收方式"
            value={codMethod}
            onChange={(e) => setCodMethod(e.target.value)}
            className={SELECT}
          >
            <option value="CASH">收了现金</option>
            <option value="TRANSFER">转账</option>
            <option value="NONE">没收到</option>
          </select>
        </label>
      )}

      {specialItems.length > 0 && (
        <div className="mb-3">
          {/* ★ 逐项勾,不是一个「都给了」——一单里既有冻品又有易碎时,
              一个勾表示不了「冻品给了、易碎那件忘在车上」 */}
          <span className="block mb-1 text-sm text-slate-600 dark:text-slate-400">
            这一单有特殊物品,逐样确认交到收件人手上了
          </span>
          {specialItems.map((i) => (
            <label key={i.code} className="flex items-center gap-2 py-1 text-sm">
              <input
                type="checkbox"
                aria-label={i.label}
                checked={handedOver.includes(i.code)}
                onChange={(e) =>
                  setHandedOver((s) =>
                    e.target.checked ? [...s, i.code] : s.filter((x) => x !== i.code),
                  )
                }
              />
              <span className="text-slate-900 dark:text-slate-100">
                {i.icon} {i.label}
              </span>
            </label>
          ))}
        </div>
      )}

      {/*
        ★ 代司机点的时候把这件事说破:界面上不写的话,
          调度员不会意识到自己正在替一个不在现场的人作证。
      */}
      {onBehalf && (
        <p
          className="mb-3 rounded-lg bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-xs
                     text-amber-800 dark:text-amber-300"
        >
          这一步本该由司机在现场点。后台代点会记下是谁点的、什么时候、为什么
          {order.driver ? `(这单的司机是 ${order.driver.name})` : ''}
        </p>
      )}

      {(def.requiresReason || onBehalf) && (
        <label className="block">
          <span className="block mb-1 text-sm text-slate-600 dark:text-slate-400">
            {onBehalf ? '为什么由后台代点(必填,会记入时间轴)' : '原因(必填,会记入时间轴)'}
          </span>
          <textarea
            aria-label="原因"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder={onBehalf ? '例如:司机手机没电,电话里确认已取货' : undefined}
            className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600
                       bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
          />
        </label>
      )}

      {proofBlocked && (
        <div
          aria-label="缺照片凭证"
          className="mt-3 rounded-lg border border-red-300 dark:border-red-800 bg-red-50
                     dark:bg-red-950/40 px-3 py-2 text-xs text-red-800 dark:text-red-300"
        >
          <div className="font-medium">{proofBlocked}</div>
          {/* ★ 说清楚「继续会留下什么」,而不只是「你确定吗」 */}
          <div className="mt-1">
            后台没有照片可传。仍然记上的话,时间轴和审计里会写成
            <span className="font-medium">「无照片凭证」</span>,并记下是你、什么时候、为什么。
            更稳妥的做法是让司机补拍一张再点。
          </div>
        </div>
      )}

      <Footer
        busy={busy}
        disabled={!ready}
        confirmLabel={proofBlocked ? '没有照片,仍然记上' : undefined}
        danger={proofBlocked != null}
        onClose={onClose}
        onSubmit={submit}
      />
    </Shell>
  );
}

function Shell({
  title,
  error,
  onClose,
  children,
}: {
  title: string;
  error: string | null;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    // z 要高于详情抽屉(z-50),否则弹窗会被抽屉盖住
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="w-full max-w-md rounded-2xl bg-white dark:bg-slate-800 p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-3 text-lg font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
        {error && (
          <div
            className="mb-3 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200
                       dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300"
          >
            {error}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

function Footer({
  busy,
  disabled,
  confirmLabel,
  danger,
  onClose,
  onSubmit,
}: {
  busy: boolean;
  disabled: boolean;
  confirmLabel?: string;
  danger?: boolean;
  onClose: () => void;
  onSubmit: () => void;
}) {
  return (
    <div className="mt-4 flex gap-3">
      <button
        onClick={onClose}
        className="flex-1 h-10 rounded-lg border border-slate-300 dark:border-slate-600
                   text-slate-700 dark:text-slate-300"
      >
        取消
      </button>
      <button
        onClick={onSubmit}
        disabled={busy || disabled}
        className={`flex-1 h-10 rounded-lg text-white font-medium disabled:opacity-40 ${
          danger ? 'bg-red-600' : 'bg-brand-600'
        }`}
      >
        {busy ? '提交中…' : (confirmLabel ?? '确认')}
      </button>
    </div>
  );
}
